/**
 * Typed fetch wrapper for the AuctionRadar API.
 *
 * In dev, requests go to /api which Vite proxies to wrangler on :8787
 * (see vite.config.ts). In production they're same-origin (Worker serves
 * both the SPA assets and the API), so the same /api prefix just works.
 *
 * `credentials: 'include'` is set unconditionally so the future HMAC
 * session cookie travels on every request without per-call opt-in.
 */
import type {
  ApiError,
  ByMunicipalityStats,
  FunnelStats,
  LeadDetail,
  LeadListItem,
  LeadMapPin,
  ListLeadsQuery,
  LoginRequest,
  LoginResponse,
  MarketPriceItem,
  MunicipalityOption,
  Page,
  Region,
  SessionUser,
  UpdateLeadStatusBody,
  UpdateSettingsBody,
} from '@auctionradar/shared';

class ApiCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiCallError';
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // Response wasn't JSON — leave body null.
    }
    throw new ApiCallError(
      res.status,
      body,
      body?.error ?? `HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

/**
 * Translate a `ListLeadsQuery` object into a URL query string. Array filters
 * become comma-joined values (matching the csvList Zod parser server-side),
 * undefined fields are omitted. Stable key ordering keeps TanStack Query
 * cache keys deterministic.
 */
function leadsQueryString(q: ListLeadsQuery): string {
  const params = new URLSearchParams();
  const set = (k: string, v: string | number | undefined): void => {
    if (v != null && v !== '') params.set(k, String(v));
  };
  const setList = (k: string, v: ReadonlyArray<string> | undefined): void => {
    if (v && v.length > 0) params.set(k, v.join(','));
  };

  setList('tier', q.tier);
  setList('status', q.status);
  setList('risk', q.risk);
  set('municipality', q.municipality);
  set('region', q.region);
  set('round', q.round);
  set('sqmMin', q.sqmMin);
  set('sqmMax', q.sqmMax);
  set('discountMin', q.discountMin);
  set('inIauction', q.inIauction == null ? undefined : String(q.inIauction));
  set('sort', q.sort);
  set('direction', q.direction);
  set('limit', q.limit);
  set('offset', q.offset);

  return params.toString();
}

export const api = {
  listLeads(q: ListLeadsQuery = {}): Promise<Page<LeadListItem>> {
    const qs = leadsQueryString(q);
    return apiFetch<Page<LeadListItem>>(`/api/leads${qs ? `?${qs}` : ''}`);
  },
  getLead(id: string): Promise<LeadDetail> {
    return apiFetch<LeadDetail>(`/api/leads/${encodeURIComponent(id)}`);
  },
  leadMapPins(): Promise<{ items: LeadMapPin[] }> {
    return apiFetch<{ items: LeadMapPin[] }>('/api/leads/map');
  },
  updateLeadStatus(id: string, body: UpdateLeadStatusBody): Promise<LeadDetail> {
    return apiFetch<LeadDetail>(`/api/leads/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  // ─── auth ───
  me(): Promise<LoginResponse> {
    return apiFetch<LoginResponse>('/api/auth/me');
  },
  login(body: LoginRequest): Promise<LoginResponse> {
    return apiFetch<LoginResponse>('/api/auth/session', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  logout(): Promise<{ ok: true }> {
    return apiFetch<{ ok: true }>('/api/auth/session', { method: 'DELETE' });
  },
  // ─── price map ───
  listMarketPrices(region?: Region): Promise<{ items: MarketPriceItem[] }> {
    const qs = region ? `?region=${region}` : '';
    return apiFetch<{ items: MarketPriceItem[] }>(`/api/market-prices${qs}`);
  },
  zonePrices(slug: string): Promise<{ items: MarketPriceItem[] }> {
    return apiFetch<{ items: MarketPriceItem[] }>(
      `/api/market-prices/zone/${encodeURIComponent(slug)}`,
    );
  },
  // ─── analytics ───
  funnel(): Promise<FunnelStats> {
    return apiFetch<FunnelStats>('/api/analytics/funnel');
  },
  byMunicipality(): Promise<{ items: ByMunicipalityStats[] }> {
    return apiFetch<{ items: ByMunicipalityStats[] }>(
      '/api/analytics/by-municipality',
    );
  },
  // ─── settings ───
  getSettings(): Promise<{
    user: SessionUser;
    municipalities: MunicipalityOption[];
  }> {
    return apiFetch<{ user: SessionUser; municipalities: MunicipalityOption[] }>(
      '/api/settings',
    );
  },
  updateSettings(body: UpdateSettingsBody): Promise<{ user: SessionUser }> {
    return apiFetch<{ user: SessionUser }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
};

export { ApiCallError };
